/**
 * SecuriteCompteModal — « Sécurité du compte » : lister, ajouter et retirer ses
 * applications d'authentification (TOTP). Port de la SecurityPage de Pimer, en fenêtre
 * ouverte depuis le menu utilisateur, comme ChangePasswordModal.
 *
 * Tout compte peut adhérer : dès qu'il porte un TOTP vérifié, le serveur ET la base
 * (politiques <table>_aal2) exigent le code à chaque connexion.
 *
 * Après chaque ajout ou retrait, la session est RAFRAÎCHIE : l'événement
 * TOKEN_REFRESHED fait relire /api/auth/me par SupabaseWrapper, qui met à jour le jeton
 * des pages (sso_token) et affiche l'enrôlement si le rôle l'exige — c'est ce qui se
 * passe quand un super_admin retire sa dernière application.
 *
 * Pas de window.confirm (bloquant, et proscrit par les outils de preuve) : la
 * confirmation du retrait est en ligne.
 *
 * Section « Techniciens du compte » (permission security.manage, portée compte) :
 * la règle qui impose la double authentification aux techniciens du compte —
 * éteinte par défaut, décision du commanditaire (2026-09-16). Elle se règle par
 * PUT /api/settings/securite ; l'app mobile et la garde serveur la lisent en base.
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase, useHasPermission } from '../SupabaseWrapper';
import { useViewport } from '../hooks/useViewport';
import BlocCleTotp, { ETAPES_ENROLEMENT } from './securite/BlocCleTotp';
import ChampCodeTotp from './securite/ChampCodeTotp';
import {
  abandonnerEnrolementTotp, EnrolementTotp, FacteurTotp, listerFacteursTotp,
  preparerEnrolementTotp, retirerFacteurTotp, verifierCodeTotp
} from './securite/totp';

interface Props {
  role?: string;
  onClose: () => void;
}

const BOUTON_SECONDAIRE: React.CSSProperties = {
  padding: '8px 14px', backgroundColor: 'white', color: '#334155', border: '1px solid #cbd5e1',
  borderRadius: '8px', fontSize: '14px', cursor: 'pointer'
};
const BOUTON_PRINCIPAL: React.CSSProperties = {
  padding: '8px 14px', backgroundColor: '#3b82f6', color: 'white', border: 'none',
  borderRadius: '8px', fontSize: '14px', fontWeight: 500, cursor: 'pointer'
};

function SecuriteCompteModal({ role, onClose }: Props) {
  const { isPhone } = useViewport();
  const [facteurs, setFacteurs] = useState<FacteurTotp[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [enrolement, setEnrolement] = useState<EnrolementTotp | null>(null);
  const [preparation, setPreparation] = useState(false);
  const [code, setCode] = useState('');
  const [erreurCode, setErreurCode] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [aRetirer, setARetirer] = useState<string | null>(null);
  const verificationEnCours = useRef(false);
  const enrolementRef = useRef<EnrolementTotp | null>(null);
  const obligatoire = role === 'super_admin';
  const peutReglerCompte = useHasPermission('security.manage', 'account');
  const [regleTechniciens, setRegleTechniciens] = useState<boolean | null>(null);
  const [regleEnCours, setRegleEnCours] = useState(false);
  const [regleMessage, setRegleMessage] = useState<{ type: 'ok' | 'erreur'; texte: string } | null>(null);

  useEffect(() => { void charger(); }, []);

  useEffect(() => {
    if (peutReglerCompte) void chargerRegle();
  }, [peutReglerCompte]);

  async function appelSecurite(methode: 'GET' | 'PUT', corps?: object) {
    const reponse = await fetch(`${window.location.origin}/api/settings/securite`, {
      method: methode,
      headers: { Authorization: `Bearer ${localStorage.getItem('sso_token')}`, 'Content-Type': 'application/json' },
      body: corps ? JSON.stringify(corps) : undefined
    });
    const json = await reponse.json().catch(() => null);
    if (!reponse.ok || !json?.success) throw new Error(json?.error?.code || `HTTP ${reponse.status}`);
    return json.securite as { techniciens_obligatoire: boolean };
  }

  async function chargerRegle() {
    try {
      setRegleTechniciens((await appelSecurite('GET')).techniciens_obligatoire);
    } catch (err) {
      console.error('[SecuriteCompte] Lecture de la règle du compte impossible:', err);
      setRegleMessage({ type: 'erreur', texte: 'Impossible de lire la règle du compte.' });
    }
  }

  async function changerRegle(valeur: boolean) {
    setRegleEnCours(true); setRegleMessage(null);
    try {
      const securite = await appelSecurite('PUT', { techniciens_obligatoire: valeur });
      setRegleTechniciens(securite.techniciens_obligatoire);
      console.log('[SecuriteCompte] Règle des techniciens :', securite.techniciens_obligatoire);
      setRegleMessage({ type: 'ok', texte: 'Règle enregistrée.' });
    } catch (err) {
      console.error('[SecuriteCompte] Enregistrement de la règle impossible:', err);
      setRegleMessage({ type: 'erreur', texte: 'Enregistrement impossible. Réessayez.' });
    } finally {
      setRegleEnCours(false);
    }
  }

  useEffect(() => {
    enrolementRef.current = enrolement;
  }, [enrolement]);

  // Un enrôlement laissé en plan (fenêtre fermée) ne doit pas rester en base.
  useEffect(() => () => {
    if (enrolementRef.current) void abandonnerEnrolementTotp(enrolementRef.current.id);
  }, []);

  useEffect(() => {
    function surTouche(e: KeyboardEvent) {
      if (e.key === 'Escape' && !enCours) fermer();
    }
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [enCours]);

  async function charger() {
    try {
      setFacteurs(await listerFacteursTotp());
    } catch (err) {
      console.error('[SecuriteCompte] Lecture des facteurs impossible:', err);
      setErreur('Impossible de lire vos applications d’authentification.');
    }
  }

  /** Nouveau jeton (aal à jour) → SupabaseWrapper relit /api/auth/me (TOKEN_REFRESHED). */
  async function rafraichirSession() {
    const { error } = await supabase.auth.refreshSession();
    if (error) console.error('[SecuriteCompte] Rafraîchissement de session échoué:', error.message);
  }

  function fermer() {
    onClose();
  }

  async function demarrerAjout() {
    setErreur(null); setInfo(null); setErreurCode(null); setCode(''); setARetirer(null);
    setPreparation(true);
    try {
      setEnrolement(await preparerEnrolementTotp());
      console.log('[SecuriteCompte] Enrôlement TOTP préparé');
    } catch (err) {
      console.error('[SecuriteCompte] Enrôlement impossible:', err);
      setErreur('Impossible de préparer une nouvelle application. Réessayez.');
    } finally {
      setPreparation(false);
    }
  }

  async function annulerAjout() {
    if (enrolement) await abandonnerEnrolementTotp(enrolement.id);
    setEnrolement(null); setCode(''); setErreurCode(null);
  }

  async function validerAjout(e: React.FormEvent) {
    e.preventDefault();
    if (!enrolement || verificationEnCours.current) return;
    verificationEnCours.current = true;
    setEnCours(true); setErreurCode(null);
    try {
      const resultat = await verifierCodeTotp(enrolement.id, code, false);
      if (resultat === 'incorrect') {
        setErreurCode('Code incorrect. Vérifiez l’heure de votre téléphone et saisissez le code affiché.');
        return;
      }
      if (resultat === 'indisponible') {
        setErreurCode('Vérification impossible pour le moment. Réessayez.');
        return;
      }
      console.log('[SecuriteCompte] Application ajoutée');
      setEnrolement(null); setCode('');
      await charger();
      await rafraichirSession();
      setInfo('Application ajoutée : le code sera demandé à chaque connexion.');
    } catch (err) {
      console.error('[SecuriteCompte] Erreur de vérification:', err);
      setErreurCode('Erreur de vérification. Réessayez.');
    } finally {
      verificationEnCours.current = false;
      setEnCours(false);
    }
  }

  async function confirmerRetrait(id: string) {
    setEnCours(true); setErreur(null); setInfo(null);
    const derniere = (facteurs || []).length === 1;
    try {
      await retirerFacteurTotp(id);
      console.log('[SecuriteCompte] Application retirée', derniere ? '(la dernière)' : '');
      setARetirer(null);
      await charger();
      await rafraichirSession();
      setInfo(derniere ? 'Application retirée : le code ne sera plus demandé.' : 'Application retirée.');
    } catch (err) {
      console.error('[SecuriteCompte] Retrait impossible:', err);
      setErreur('Retrait impossible. Reconnectez-vous avec votre code, puis réessayez.');
    } finally {
      setEnCours(false);
    }
  }

  const actif = (facteurs || []).length > 0;

  function avertissementRetrait(): string {
    const derniere = (facteurs || []).length === 1;
    if (!derniere) return 'Retirer cette application ? Les autres continueront de fournir le code.';
    if (obligatoire) {
      return 'C’est votre dernière application. La double authentification est obligatoire pour votre rôle : vous devrez en configurer une nouvelle immédiatement.';
    }
    return 'C’est votre dernière application : votre compte ne sera plus protégé que par son mot de passe.';
  }

  return createPortal(
    <div
      onClick={() => { if (!enCours) fermer(); }}
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1400, padding: '20px' }}
    >
      <div
        data-securite-compte
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: isPhone ? 'calc(100vw - 24px)' : '520px', width: '100%', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto',
          backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 20px 50px rgba(0,0,0,0.25)', padding: isPhone ? '20px' : '32px', boxSizing: 'border-box'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1e293b', margin: '0 0 4px' }}>Sécurité du compte</h2>
            <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>Double authentification par application (code à 6 chiffres)</p>
          </div>
          <button type="button" onClick={fermer} disabled={enCours} aria-label="Fermer" style={{ background: 'none', border: 'none', fontSize: '22px', lineHeight: 1, color: '#94a3b8', cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '12px 14px', borderRadius: '8px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', color: '#334155' }}>
            <div style={{ fontWeight: 600 }}>Double authentification</div>
            <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>
              {obligatoire
                ? 'Elle est obligatoire pour votre rôle.'
                : actif ? 'Le code est demandé à chaque connexion.' : 'Votre compte n’est protégé que par son mot de passe.'}
            </div>
          </div>
          {facteurs !== null && (
            <span data-etat-2fa style={{
              padding: '4px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap',
              backgroundColor: actif ? '#dcfce7' : '#fef3c7', color: actif ? '#166534' : '#92400e'
            }}>
              {actif ? 'Activée' : 'Désactivée'}
            </span>
          )}
        </div>

        {erreur && <p role="alert" style={{ color: '#dc2626', fontSize: '14px', margin: '0 0 12px' }}>{erreur}</p>}
        {info && <p role="status" style={{ color: '#166534', fontSize: '14px', margin: '0 0 12px' }}>{info}</p>}

        {facteurs === null ? (
          <p style={{ color: '#64748b', fontSize: '14px' }}>Chargement…</p>
        ) : facteurs.length > 0 && (
          <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '16px' }}>
            {facteurs.map((f, i) => (
              <div key={f.id} data-facteur-totp style={{ padding: '12px 14px', borderTop: i === 0 ? 'none' : '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: '#1e293b' }}>Application d’authentification {facteurs.length > 1 ? i + 1 : ''}</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>Ajoutée le {new Date(f.creeLe).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })}</div>
                  </div>
                  {aRetirer !== f.id && (
                    <button type="button" data-retirer-facteur onClick={() => { setARetirer(f.id); setInfo(null); }} disabled={enCours || !!enrolement} style={{ ...BOUTON_SECONDAIRE, color: '#b91c1c', borderColor: '#fecaca' }}>
                      Retirer
                    </button>
                  )}
                </div>
                {aRetirer === f.id && (
                  <div style={{ marginTop: '10px', padding: '10px 12px', borderRadius: '8px', backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
                    <p data-avertissement-retrait style={{ margin: '0 0 10px', fontSize: '13px', color: '#7f1d1d', lineHeight: 1.5 }}>{avertissementRetrait()}</p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button type="button" data-confirmer-retrait onClick={() => void confirmerRetrait(f.id)} disabled={enCours} style={{ ...BOUTON_PRINCIPAL, backgroundColor: '#dc2626' }}>
                        {enCours ? 'Retrait…' : 'Confirmer le retrait'}
                      </button>
                      <button type="button" onClick={() => setARetirer(null)} disabled={enCours} style={BOUTON_SECONDAIRE}>Annuler</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {facteurs !== null && !enrolement && (
          <button type="button" data-ajouter-totp onClick={() => void demarrerAjout()} disabled={preparation || enCours} style={{ ...BOUTON_PRINCIPAL, opacity: preparation ? 0.6 : 1 }}>
            {preparation ? 'Préparation…' : actif ? 'Ajouter une autre application' : 'Activer la double authentification'}
          </button>
        )}

        {enrolement && (
          <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px' }}>
            <ol style={{ color: '#334155', fontSize: '14px', paddingLeft: '20px', margin: '0 0 14px', lineHeight: 1.6 }}>
              {ETAPES_ENROLEMENT.map((etape) => <li key={etape}>{etape}</li>)}
            </ol>
            <BlocCleTotp enrolement={enrolement} />
            <form onSubmit={validerAjout} style={{ marginTop: '16px' }}>
              <ChampCodeTotp valeur={code} onChange={setCode} autoFocus />
              {erreurCode && <p role="alert" style={{ color: '#dc2626', fontSize: '14px', margin: '10px 0 0' }}>{erreurCode}</p>}
              <div style={{ display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap' }}>
                <button type="submit" data-valider-totp disabled={enCours || code.length !== 6} style={{ ...BOUTON_PRINCIPAL, opacity: enCours || code.length !== 6 ? 0.6 : 1 }}>
                  {enCours ? 'Vérification…' : 'Activer'}
                </button>
                <button type="button" onClick={() => void annulerAjout()} disabled={enCours} style={BOUTON_SECONDAIRE}>Annuler</button>
              </div>
            </form>
          </div>
        )}

        {peutReglerCompte && (
          <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b', margin: '0 0 4px' }}>Techniciens du compte</h3>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 12px', lineHeight: 1.5 }}>
              Règle valable pour tous les techniciens de votre compte, sur l’application mobile.
            </p>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: regleEnCours || regleTechniciens === null ? 'default' : 'pointer' }}>
              <input
                type="checkbox"
                data-regle-techniciens
                checked={regleTechniciens === true}
                disabled={regleEnCours || regleTechniciens === null}
                onChange={(e) => void changerRegle(e.target.checked)}
                style={{ marginTop: '3px', width: '16px', height: '16px', cursor: 'inherit' }}
              />
              <span style={{ fontSize: '14px', color: '#334155', lineHeight: 1.5 }}>
                <strong style={{ fontWeight: 600 }}>Exiger la double authentification des techniciens</strong>
                <br />
                <span style={{ fontSize: '13px', color: '#64748b' }}>
                  À leur prochaine connexion, ils devront configurer une application d’authentification, puis saisir son code à chaque connexion.
                </span>
              </span>
            </label>
            {regleMessage && (
              <p role={regleMessage.type === 'erreur' ? 'alert' : 'status'} style={{ fontSize: '13px', margin: '10px 0 0', color: regleMessage.type === 'erreur' ? '#dc2626' : '#166534' }}>
                {regleMessage.texte}
              </p>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default SecuriteCompteModal;
